
import { useCallback, useState } from "react";
import { motion } from "framer-motion";

import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCommentDots,
  FaUserTag,
} from "react-icons/fa";

import TurnstileWidget from "./TurnstileWidget";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    userType: "Student",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  // Cloudflare Turnstile state
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  // Validate form fields
  const validate = () => {
    const newErrors = {};

    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const message = formData.message.trim();

    if (!name) {
      newErrors.name = "Name is required";
    }

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!phone) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "Phone must be 10 digits";
    }

    if (!message) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Submit contact form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sending) return;

    if (!validate()) {
      return;
    }

    // Require a completed Turnstile challenge
    if (!turnstileToken) {
      setSubmitted(false);
      setStatus(
        "⚠️ Please complete the security verification."
      );
      return;
    }

    setSending(true);
    setSubmitted(false);
    setStatus("Sending...");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          userType: formData.userType,
          message: formData.message.trim(),
          turnstileToken,
        }),
      });

      let result = {};

      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Unable to send your message."
        );
      }

      // Success
      setStatus("✅ Message sent successfully!");
      setSubmitted(true);

      setFormData({
        name: "",
        email: "",
        phone: "",
        userType: "Student",
        message: "",
      });

      setErrors({});

      window.setTimeout(() => {
        setSubmitted(false);
        setStatus("");
      }, 5000);
    } catch (error) {
      console.error("Contact form error:", error);

      setSubmitted(false);

      setStatus(
        error.message ||
          "⚠️ Failed to send message. Please try again later."
      );
    } finally {
      setSending(false);

      // A Turnstile token must not be reused.
      // Remount the widget to request a fresh token.
      setTurnstileToken("");
      setTurnstileKey((previous) => previous + 1);
    }
  };

  const fields = [
    {
      name: "name",
      type: "text",
      placeholder: "Full Name",
      icon: <FaUser />,
    },
    {
      name: "email",
      type: "email",
      placeholder: "Email Address",
      icon: <FaEnvelope />,
    },
    {
      name: "phone",
      type: "tel",
      placeholder: "Phone Number",
      icon: <FaPhone />,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 w-full max-w-xl mx-auto bg-white/80 dark:bg-gray-900/90 backdrop-blur-2xl rounded-2xl p-10 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)] border border-transparent bg-clip-padding"
      style={{
        borderImage:
          "linear-gradient(135deg, #2563eb, #7c3aed, #0ea5e9) 1",
      }}
    >
      <div className="text-center mb-6">
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Fill out your details and we’ll get back to you shortly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {fields.map((field) => (
          <div
            key={field.name}
            className="relative group"
          >
            <span className="absolute left-3 top-3 text-blue-500 text-lg">
              {field.icon}
            </span>

            <input
              type={field.type}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              placeholder={field.placeholder}
              required
              disabled={sending}
              autoComplete={
                field.name === "name"
                  ? "name"
                  : field.name === "email"
                    ? "email"
                    : "tel"
              }
              className="w-full pl-10 pr-3 py-3 bg-white/60 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 dark:text-gray-200 placeholder-gray-400 disabled:opacity-70 disabled:cursor-not-allowed"
            />

            {errors[field.name] && (
              <p className="text-red-500 text-xs mt-1">
                {errors[field.name]}
              </p>
            )}
          </div>
        ))}

        <div className="relative">
          <FaUserTag className="absolute left-3 top-3 text-blue-500 text-lg" />

          <select
            name="userType"
            value={formData.userType}
            onChange={handleChange}
            disabled={sending}
            className="w-full pl-10 pr-3 py-3 bg-white/60 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 dark:text-gray-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <option value="Student">Student</option>
            <option value="Professional">Professional</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="relative">
          <FaCommentDots className="absolute left-3 top-3 text-blue-500 text-lg" />

          <textarea
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Write your message..."
            required
            disabled={sending}
            className="w-full pl-10 pr-3 py-3 bg-white/60 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none text-gray-800 dark:text-gray-200 placeholder-gray-400 disabled:opacity-70 disabled:cursor-not-allowed"
          />

          {errors.message && (
            <p className="text-red-500 text-xs mt-1">
              {errors.message}
            </p>
          )}
        </div>

        {/* Cloudflare Turnstile security verification */}
        <div className="flex justify-center overflow-x-auto">
          <TurnstileWidget
            key={turnstileKey}
            onVerify={handleTurnstileVerify}
          />
        </div>

        <motion.button
          whileHover={
            !sending
              ? {
                  scale: 1.05,
                  boxShadow:
                    "0px 0px 15px rgba(59,130,246,0.5)",
                }
              : undefined
          }
          whileTap={!sending ? { scale: 0.97 } : undefined}
          type="submit"
          disabled={sending || submitted}
          className="relative w-full py-3 font-semibold text-white rounded-lg bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-indigo-600 hover:to-blue-600 shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {sending
            ? "Sending..."
            : submitted
              ? "Message Sent ✅"
              : "Send Message"}
        </motion.button>
      </form>

      {status && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          role="status"
          aria-live="polite"
          className={`mt-5 text-center text-sm font-medium ${
            status.includes("✅")
              ? "text-green-600"
              : status.includes("⚠️")
                ? "text-yellow-500"
                : status.includes("❌")
                  ? "text-red-600"
                  : "text-blue-600"
          }`}
        >
          {status}
        </motion.p>
      )}
    </motion.div>
  );
};

export default ContactForm;
